import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, Image, TextInput, Modal, Alert, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MediaManagerStyles as mediaManagerStyles, folderColors } from '../styles/MediaManagerStyles';
import { MediaViewer } from './MediaViewer';
import * as DocumentPicker from 'expo-document-picker';
import { Ionicons } from '@expo/vector-icons';
import { getFirestore, doc, updateDoc, Timestamp, collection, query, where, getDocs, getDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { addFileToUserUploads, getOrCreateUserUploadsFolder } from '../utils/fileManagerUtils';
import { VenueCardFile } from './VenueCardFile';
import { ReportCardFile } from './ReportCardFile';
import { ItemWrapper } from './ItemWrapper';
import { useUser } from './LoginPersistence';



const getFileType = (item) => {
    // New structure: check data.fileType first
    if (item.data && item.data.fileType) {
        return item.data.fileType;
    }
    
    // Legacy structure fallback
    if (item.mediaType) {
        return item.mediaType;
    }
    
    // Fallback to file name extension if no fileType
    if (!item.name || typeof item.name !== 'string') {
        return 'file';
    }
    
    const extension = item.name.split('?')[0].split('.').pop().toLowerCase();
    
    // Image types
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
        return 'image';
    }

    // PDF
    if (extension === 'pdf') {
        return 'pdf';
    }

    // Audio types
    if (['mp3', 'wav', 'aac', 'flac', 'ogg', 'm4a', 'wma'].includes(extension)) {
        return 'audio';
    }

    // Video types
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
        return 'video';
    }

    // Document types
    if (['doc', 'docx', 'txt', 'rtf'].includes(extension)) {
        return 'document';
    }

    // Default to image for files without clear extensions
    return 'image';
};


const MediaManager = ({ userData, setUser, onEditReport, onDeleteReport, onViewVenue, onAddVenuePhoto, onDeleteVenuePhoto }) => {
    const [showCreateFolder, setShowCreateFolder] = useState(false);
    const [folders, setFolders] = useState(userData?.folders || {});
    const [files, setFiles] = useState(userData?.files || {});
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [currentFolderId, setCurrentFolderId] = useState(null); // null = root level
    const [folderPath, setFolderPath] = useState([]); // Array of folder objects for breadcrumb
    const [newFolder, setNewFolder] = useState({
        name: '',
        color: '#0bb5df',
        backgroundImg: null
    });
    const [loading, setLoading] = useState(false);
    const mediaManagerRef = useRef(null);
    
    // Store navigation state in refs to prevent loss during re-renders
    const navigationStateRef = useRef({
        currentFolderId: null,
        folderPath: []
    });

    // Selection state
    const [selectedItem, setSelectedItem] = useState(null); // { id, type, item }
    const [lastTap, setLastTap] = useState(null);

    // Context menu state
    const [contextMenu, setContextMenu] = useState(null); // { item: {id, type, item}, position: {x, y} }
    const [showRenameModal, setShowRenameModal] = useState(null); // item being renamed
    const [renameText, setRenameText] = useState('');
    const [showColorModal, setShowColorModal] = useState(null); // folder being recolored
    const [selectedColor, setSelectedColor] = useState('#0bb5df');
    const [showDeleteFileModal, setShowDeleteFileModal] = useState(null); // file being deleted
    const [showSuccessModal, setShowSuccessModal] = useState(null); // success message
    const [showErrorModal, setShowErrorModal] = useState(null); // error message
    const [selectedMediaIndex, setSelectedMediaIndex] = useState(null);
    const [isMediaViewerVisible, setIsMediaViewerVisible] = useState(false);
    const [mediaViewerMedia, setMediaViewerMedia] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isDragOver, setIsDragOver] = useState(false);
    const [hoverDropFolderId, setHoverDropFolderId] = useState(null);
    const hoverDropFolderRef = useRef(null);


    // Handle touch-based drop from Profile component
    const handleTouchDrop = (data) => {
        setIsDragOver(false);
        
        if (data.dragType === 'venue') {
            addVenueToMediaManager(data);
        } else if (data.dragType === 'report') {
            addReportToMediaManager(data);
        } else if (data.dragType === 'media') {
            addMediaToMediaManager(data);
        }
    };

    // Set up global drop handler and drag over handler
    useEffect(() => {
        window.mediaManagerDropHandler = handleTouchDrop;
        window.mediaManagerDragOverHandler = (isOver) => {
            setIsDragOver(isOver);
        };
        window.mediaManagerHoverFolderRef = hoverDropFolderRef;
        // Set global reference to MediaManager element
        window.mediaManagerElement = mediaManagerRef.current;
        return () => {
            window.mediaManagerDropHandler = null;
            window.mediaManagerDragOverHandler = null;
            window.mediaManagerHoverFolderRef = null;
            window.mediaManagerElement = null;
        };
    }, []);

    // Double tap handler
    const handleItemPress = (itemId, itemType, item, onDoublePress) => {
        const now = Date.now();
        const DOUBLE_PRESS_DELAY = 300;

        if (lastTap && (now - lastTap) < DOUBLE_PRESS_DELAY && selectedItem && selectedItem.id === itemId) {
            // Double tap detected
            onDoublePress();
            setLastTap(null);
        } else {
            // Single tap
            setSelectedItem({
                id: itemId,
                type: itemType,
                item: item
            });
            setLastTap(now);
        }
    };

    // Media viewer handlers
    const handleMediaPress = (index) => {
        setMediaViewerMedia(getCurrentFolderImages());
        setSelectedMediaIndex(index);
        setIsMediaViewerVisible(true);
    };

    const handleMediaViewerClose = () => {
        setIsMediaViewerVisible(false);
        setSelectedMediaIndex(null);
        setMediaViewerMedia(null);
    };

    // Get current folder's images for media viewer
    const getCurrentFolderImages = () => {
        const folderFiles = getCurrentFiles();
        const imageFiles = folderFiles.filter(([_, file]) => file.url);
        
        // Return as object with file names as keys and URLs as values (for MediaViewer compatibility)
        const imageObject = {};
        imageFiles.forEach(([fileId, file], index) => {
            imageObject[file.name || `image_${index}`] = file.url;
        });
        
        return imageObject;
    };

    // Function to fetch full report data from database
    const fetchFullReportData = async (reportId) => {
        try {
            const db = getFirestore();
            const reportRef = doc(db, 'venue_reports', reportId);
            const reportDoc = await getDoc(reportRef);
            
            if (reportDoc.exists()) {
                return reportDoc.data();
            } else {
                console.error('Report not found:', reportId);
                return null;
            }
        } catch (error) {
            console.error('Error fetching report data:', error);
            return null;
        }
    };

    const getCurrentFolderMedia = () => {
        const folderFiles = getCurrentFiles();
        const mediaFiles = folderFiles.map(([fileId, file]) => {
            const itemType = file.itemType;
            
            if (itemType === 'savedVenue') {
                return {
                    id: fileId,
                    itemType: 'savedVenue',
                    ...file.data,
                    imageUrl: file.data?.imageUrl || file.data?.url
                };
            } else if (itemType === 'report') {
                return {
                    id: fileId,
                    itemType: 'report',
                    ...file.data,
                    // Note: Full report data will be fetched when the report is opened
                    // This returns the reference data for display purposes
                };
            } else {
                return {
                    id: fileId,
                    itemType: 'file',
                    name: file.name || (file.data?.url ? file.data.url.split('/').pop().split('?')[0] : 'Unknown'),
                    url: file.data?.url || file.url,
                    type: getFileType(file)
                };
            }
        });
        return mediaFiles;
    };

    const migrateExistingMedia = async () => {
        setLoading(true);
        
        try {
            const db = getFirestore();
            
            // Check if this is a bulk migration (admin) or single user migration
            const shouldMigrateAllUsers = window.confirm(
                'Migration Options:\n\n' +
                'OK = Migrate ALL users in the system (ADMIN ONLY)\n' +
                'Cancel = Migrate only current user\n\n' +
                'WARNING: Bulk migration will process ALL users and cannot be undone!'
            );
            
            await runChangePermissions(db);
            // if (shouldMigrateAllUsers) {
            // } else {
            //     await runSingleUserMigration(db);
            // }
            
        } catch (error) {
            console.error('Migration error:', error);
            Alert.alert('Migration Error', 'Failed to migrate media files. Please try again.\n\nError: ' + error.message);
        } finally {
            setLoading(false);
        }
    };


    // Bulk migration for all users
    const runBulkMigration = async (db) => {
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const totalUsers = usersSnapshot.size;
        let processedUsers = 0;
        let successfulMigrations = 0;
        let failedMigrations = 0;
        let totalFilesProcessed = 0;
        const errors = [];
        
        // Process each user
        for (const userDoc of usersSnapshot.docs) {
            try {
                const currentUserData = userDoc.data();
                const currentUserId = userDoc.id;
                
                const result = await migrateSingleUser(db, currentUserId, currentUserData);
                
                if (result.success) {
                    successfulMigrations++;
                    totalFilesProcessed += result.totalFiles;
                } else {
                    failedMigrations++;
                    errors.push({
                        userId: currentUserId,
                        userEmail: currentUserData.email,
                        error: result.error
                    });
                    console.error(`❌ Failed to migrate user ${currentUserData.email || currentUserId}: ${result.error}`);
                }
                
                processedUsers++;
                
                // Small delay to avoid overwhelming the database
                await new Promise(resolve => setTimeout(resolve, 100));
                
            } catch (error) {
                failedMigrations++;
                errors.push({
                    userId: userDoc.id,
                    userEmail: userDoc.data().email,
                    error: error.message
                });
                console.error(`❌ Error processing user ${userDoc.id}:`, error);
                processedUsers++;
            }
        }

        
        
        // Show final results
        const successRate = Math.round((successfulMigrations / processedUsers) * 100);
        const summary = 
            `🎉 BULK MIGRATION COMPLETED!\n\n` +
            `📊 Results:\n` +
            `• Total Users: ${totalUsers}\n` +
            `• Processed: ${processedUsers}\n` +
            `• Successful: ${successfulMigrations}\n` +
            `• Failed: ${failedMigrations}\n` +
            `• Files Processed: ${totalFilesProcessed}\n` +
            `• Success Rate: ${successRate}%\n\n` +
            (errors.length > 0 ? `❌ ${errors.length} errors occurred (check console for details)` : '✅ No errors!');
        
        
        Alert.alert('Bulk Migration Complete', summary);
    };

    const runChangePermissions = async (db) => {
        
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const totalUsers = usersSnapshot.size;
        let processedUsers = 0;
        let successfulMigrations = 0;
        let failedMigrations = 0;
        let totalFilesProcessed = 0;
        const errors = [];
        
        // Process each user
        for (const userDoc of usersSnapshot.docs) {
            try {
                const currentUserData = userDoc.data();
                const currentUserId = userDoc.id;
                
                const result = await changePermissions(db, currentUserId, currentUserData);
                
                if (result.success) {
                    successfulMigrations++;
                    totalFilesProcessed += result.totalFiles;
                } else {
                    failedMigrations++;
                    errors.push({
                        userId: currentUserId,
                        userEmail: currentUserData.email,
                        error: result.error
                    });
                    console.error(`❌ Failed to migrate user ${currentUserData.email || currentUserId}: ${result.error}`);
                }
                
                processedUsers++;
                
                // Small delay to avoid overwhelming the database
                await new Promise(resolve => setTimeout(resolve, 100));
                
            } catch (error) {
                failedMigrations++;
                errors.push({
                    userId: userDoc.id,
                    userEmail: userDoc.data().email,
                    error: error.message
                });
                console.error(`❌ Error processing user ${userDoc.id}:`, error);
                processedUsers++;
            }
        }

        
        
        // Show final results
        const successRate = Math.round((successfulMigrations / processedUsers) * 100);
        const summary = 
            `🎉 BULK MIGRATION COMPLETED!\n\n` +
            `📊 Results:\n` +
            `• Total Users: ${totalUsers}\n` +
            `• Processed: ${processedUsers}\n` +
            `• Successful: ${successfulMigrations}\n` +
            `• Failed: ${failedMigrations}\n` +
            `• Files Processed: ${totalFilesProcessed}\n` +
            `• Success Rate: ${successRate}%\n\n` +
            (errors.length > 0 ? `❌ ${errors.length} errors occurred (check console for details)` : '✅ No errors!');
        
        Alert.alert('Bulk Migration Complete', summary);
    };

    // Single user migration
    const runSingleUserMigration = async (db) => {
        
        const result = await migrateSingleUser(db, userData.uid, userData);
        
        if (result.success && result.totalFiles > 0) {
            Alert.alert(
                'Migration Complete!', 
                `Successfully migrated ${result.totalFiles} files.\n\n` +
                `• ${result.profileFiles} profile media files moved to root\n` +
                `• ${result.reportFiles} report files moved to User Uploads folder\n` +
                `• ${result.venueImageFiles} venue images moved to User Uploads folder`
            );
            
            // Update local state for current user
            if (result.updatedFolders) setFolders(result.updatedFolders);
            if (result.updatedFiles) setFiles(result.updatedFiles);
            
        } else if (result.success) {
            Alert.alert('Migration Complete', 'No files found that needed migration.');
        } else {
            throw new Error(result.error);
        }
    };

    const changePermissions = async (db, userId, currentUserData) => {
        try {
            const userRef = doc(db, 'users', userId);
            const existingFiles = currentUserData.files || {};

            // Change permissions for all files to make them non-protected
            let filesProcessed = 0;
            for (const fileId in existingFiles) {
                const file = existingFiles[fileId];
                if (file.protected === true) {
                file.protected = false;
                    filesProcessed++;
                }
            }

            // Only update if there were files to process
            if (filesProcessed > 0) {
            await updateDoc(userRef, {
                files: existingFiles
            });
            }

            return {
                success: true,
                totalFiles: filesProcessed
            };

        } catch (error) {
            return {
                success: false,
                error: error.message
            };
        }
    };


    // Reset all media data for all users (DANGEROUS - ADMIN ONLY)
    const resetAllMediaData = async () => {
        // Multiple confirmation dialogs to prevent accidental execution
 
        
        setLoading(true);
        
        try {
            const db = getFirestore();
            const storage = getStorage();
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const totalUsers = usersSnapshot.size;
            let processedUsers = 0;
            let successfulResets = 0;
            let failedResets = 0;
            let totalFilesDeleted = 0;
            let totalFoldersDeleted = 0;
            const errors = [];
            
            // Process each user
            for (const userDoc of usersSnapshot.docs) {
                try {
                    const currentUserData = userDoc.data();
                    const currentUserId = userDoc.id;
                    
                    const existingFolders = currentUserData.folders || {};
                    const existingFiles = currentUserData.files || {};
                    
                    // Keep only protected folders
                    const protectedFolders = {};
                    const foldersToDelete = [];
                    
                    // Delete all files from storage and database
                    let userFilesDeleted = 0;
                    const filesToDelete = Object.entries(existingFiles);
                    
                    // Prepare update data
                    const updateData = {
                        folders: protectedFolders,
                        files: {}, // Clear all files
                        migrationCompleted: false,
                        migrationDate: null
                    };
                    
                    // Update database
                        const userRef = doc(db, 'users', currentUserId);
                        await updateDoc(userRef, updateData);
                    
                    totalFilesDeleted += userFilesDeleted;
                    totalFoldersDeleted += foldersToDelete.length;
                    successfulResets++;
                    processedUsers++;
                    
                    // Small delay to avoid overwhelming the database
                    await new Promise(resolve => setTimeout(resolve, 100));
                    
                } catch (error) {
                    failedResets++;
                    errors.push({
                        userId: userDoc.id,
                        userEmail: userDoc.data().email,
                        error: error.message
                    });
                    console.error(`❌ Error resetting user ${userDoc.id}:`, error);
                    processedUsers++;
                }
            }
            
            // Show final results
            const successRate = Math.round((successfulResets / processedUsers) * 100);
            const summary = 
                `🚨 RESET OPERATION COMPLETED!\n\n` +
                `📊 Results:\n` +
                `• Total Users: ${totalUsers}\n` +
                `• Processed: ${processedUsers}\n` +
                `• Successful: ${successfulResets}\n` +
                `• Failed: ${failedResets}\n` +
                `• Files Deleted: ${totalFilesDeleted}\n` +
                `• Folders Deleted: ${totalFoldersDeleted}\n` +
                `• Success Rate: ${successRate}%\n\n` +
                `✅ Only protected folders remain!\n` +
                `✅ All files have been deleted from storage!\n\n` +
                (errors.length > 0 ? `❌ ${errors.length} errors occurred (check console for details)` : '✅ No errors!');
            
            
            // Update local state for current user
            setFolders({});
            setFiles({});
            
            setShowSuccessModal(summary);
            
        } catch (error) {
            console.error('❌ Reset operation error:', error);
            setShowErrorModal(`Failed to reset media data: ${error.message}\n\nCheck the console for more details.`);
        } finally {
            setLoading(false);
        }
    };

    // Set all files to protected = false for all users
    const setAllFilesUnprotected = async () => {
        const confirm = window.confirm(
            '⚠️ Set All Files to Unprotected ⚠️\n\n' +
            'This will set protected = false for ALL files in ALL users\' media managers.\n\n' +
            '• This will make all files deletable\n' +
            '• Only folders with protected = true will remain protected\n' +
            '• This affects ALL users in the system\n\n' +
            'Are you sure you want to continue?'
        );
        
        if (!confirm) return;
        
        setLoading(true);
        
        try {
            const db = getFirestore();
            const usersSnapshot = await getDocs(collection(db, 'users'));
            const totalUsers = usersSnapshot.size;
            let processedUsers = 0;
            let successfulUpdates = 0;
            let failedUpdates = 0;
            let totalFilesUpdated = 0;
            const errors = [];
            
            // Process each user
            for (const userDoc of usersSnapshot.docs) {
                try {
                    const currentUserData = userDoc.data();
                    const currentUserId = userDoc.id;
                    
                    const existingFiles = currentUserData.files || {};
                    const filesToUpdate = {};
                    let userFilesUpdated = 0;
                    
                    // Update each file to set protected = false
                    Object.entries(existingFiles).forEach(([fileId, file]) => {
                        filesToUpdate[fileId] = {
                            ...file,
                            protected: false
                        };
                        userFilesUpdated++;
                    });
                    
                    // Update database if there are files to update
                    if (userFilesUpdated > 0) {
                        const userRef = doc(db, 'users', currentUserId);
                        await updateDoc(userRef, { files: filesToUpdate });
                    }
                    
                    totalFilesUpdated += userFilesUpdated;
                    successfulUpdates++;
                    processedUsers++;
                    
                    // Small delay to avoid overwhelming the database
                    await new Promise(resolve => setTimeout(resolve, 100));
                    
                } catch (error) {
                    failedUpdates++;
                    errors.push({
                        userId: userDoc.id,
                        userEmail: userDoc.data().email,
                        error: error.message
                    });
                    console.error(`❌ Error updating user ${userDoc.id}:`, error);
                    processedUsers++;
                }
            }
            
            // Show final results
            const successRate = totalUsers > 0 ? ((successfulUpdates / totalUsers) * 100).toFixed(1) : 0;
            const summary = `✅ Operation Complete!\n\n` +
                `📊 Results:\n` +
                `• Total users processed: ${processedUsers}\n` +
                `• Successful updates: ${successfulUpdates}\n` +
                `• Failed updates: ${failedUpdates}\n` +
                `• Total files updated: ${totalFilesUpdated}\n` +
                `• Success rate: ${successRate}%\n\n` +
                `All files are now unprotected and can be deleted.`;
            
            
            // Update local state for current user
            const updatedFiles = {};
            Object.entries(files).forEach(([fileId, file]) => {
                updatedFiles[fileId] = {
                    ...file,
                    protected: false
                };
            });
            setFiles(updatedFiles);
            
            setShowSuccessModal(summary);
            
        } catch (error) {
            console.error('❌ Update operation error:', error);
            setShowErrorModal(`Failed to update files: ${error.message}\n\nCheck the console for more details.`);
        } finally {
            setLoading(false);
        }
    };

    // Helper function to determine target folder for migration
    const getTargetFolderForMigration = async () => {
        // If hovering a folder tile, prefer that as the target
        if (hoverDropFolderRef.current) return hoverDropFolderRef.current;
        if (hoverDropFolderId) return hoverDropFolderId;
        // Use ref as primary source, fallback to state
        const refState = navigationStateRef.current;
        let effectiveCurrentFolderId = refState.currentFolderId || currentFolderId;
        let effectiveFolderPath = refState.folderPath.length > 0 ? refState.folderPath : folderPath;
        // Backup check: if effectiveCurrentFolderId is null but we have a folderPath, use the last folder in the path
        if (effectiveCurrentFolderId === null && effectiveFolderPath.length > 0) {
            const lastFolderInPath = effectiveFolderPath[effectiveFolderPath.length - 1];
            effectiveCurrentFolderId = lastFolderInPath.id;
        }
        
        // If we're in the root (effectiveCurrentFolderId is null), use User Uploads folder
        if (effectiveCurrentFolderId === null) {
            return null; // Will be handled by getOrCreateUserUploadsFolder
        }
        
        // Fetch the latest folder data from the database to ensure we have the most up-to-date information
        let currentFolders = userData?.folders || folders;
        
        // If the folder doesn't exist in our current data, fetch from database
        if (!currentFolders[effectiveCurrentFolderId]) {
            try {
                const userRef = doc(db, 'users', userData.uid);
                const userDoc = await getDoc(userRef);
                const latestUserData = userDoc.data();
                currentFolders = latestUserData.folders || currentFolders;
            } catch (error) {
                console.error('Error fetching latest folder data:', error);
                // Fall back to current data
            }
        }
        
        // Check if current folder is within User Uploads folder hierarchy
        const isWithinUserUploads = (folderId) => {
            if (!folderId) return false;
            
            const folder = currentFolders[folderId];
            if (!folder) return false;
            
            // Check if this folder is the User Uploads folder itself
            if (folder.protected === true && folder.name === 'User Uploads') {
                return true;
            }
            
            // Check if this folder is a child of User Uploads
            if (folder.parentId) {
                return isWithinUserUploads(folder.parentId);
            }
            
            return false;
        };
        
        // Use the current folder if available; otherwise fallback to User Uploads
        // This allows drops into any open folder (not only within User Uploads)
        if (effectiveCurrentFolderId) {
            return effectiveCurrentFolderId;
        }
        
        // Root (no folder open) → caller will fallback to User Uploads
        return null;
    };

    // Add venue to Media Manager as a complete entity
    const addVenueToMediaManager = async (venueData) => {
        try {
            setLoading(true);
            const db = getFirestore();
            const auth = getAuth();
            const userId = auth.currentUser?.uid;
            
            if (!userId) {
                Alert.alert('Error', 'User not authenticated');
                return;
            }

            // Get current user data from database to ensure we have the latest state
            const userRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userRef);
            const userDataLatest = userDoc.data();
            const currentFiles = userDataLatest?.files || {};
            const currentFolders = userDataLatest?.folders || {};

            // Determine target folder
            const targetFolderId = await getTargetFolderForMigration();
            let userUploadsFolderId;
            
            if (targetFolderId) {
                userUploadsFolderId = targetFolderId;
            } else {
                userUploadsFolderId = await getOrCreateUserUploadsFolder(userId);
            }


            const venueId = venueData.id;
            const savedVenueImage = userData.savedVenueImages ? userData.savedVenueImages[venueId] : null;
            

            
            try {
                // Get current user data from database to ensure we have the latest state
                
                // Create venue entry in files using new structure
                const fileId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                const venueFileData = {
                    name: venueData.name,
                    id: fileId,
                    parentId: userUploadsFolderId,
                    itemType: 'savedVenue',
                    data: {
                        id: venueData.id,
                        name: venueData.name || '',
                        city: venueData.city || '',
                        state: venueData.state || '',
                        capacity: venueData.capacity || '',
                        imageUrl: savedVenueImage || '',
                        url: savedVenueImage || ''  
                    },
                    order: Object.values(currentFiles).filter(f => f.parentId === userUploadsFolderId).length,
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now()
                };
                
                
                const userRef = doc(db, 'users', userId);
                const userDoc = await getDoc(userRef);
                const currentUserData = userDoc.data();
                const updatedFiles = { ...currentUserData.files, [fileId]: venueFileData };
                await updateDoc(userRef, { files: updatedFiles });
                setFiles(updatedFiles);

                const updatedFolders = currentUserData.folders || {};
                setFolders(updatedFolders);

                setUser({
                    ...userData,
                    files: updatedFiles,
                    folders: updatedFolders
                });

                
            } catch (error) {
                console.error('Error adding venue to Media Manager:', error);
                Alert.alert('Error', `Failed to add venue: ${error.message}`);
            }
            
        } catch (error) {
            console.error('Error adding venue to Media Manager:', error);
            Alert.alert('Error', `Failed to add venue: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Add report to Media Manager as a complete entity
    const addReportToMediaManager = async (reportData) => {
        try {
            setLoading(true);
            const db = getFirestore();
            const auth = getAuth();
            const userId = auth.currentUser?.uid;
            
            if (!userId) {
                Alert.alert('Error', 'User not authenticated');
                return;
            }

            // Get current user data from database to ensure we have the latest state
            const userRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userRef);
            const userDataLatest = userDoc.data();
            const currentFiles = userDataLatest?.files || {};

            // Determine target folder
            const targetFolderId = await getTargetFolderForMigration();
            let userUploadsFolderId;
            
            if (targetFolderId) {
                userUploadsFolderId = targetFolderId;
            } else {
                userUploadsFolderId = await getOrCreateUserUploadsFolder(userId);
            }

            try {
                // Fetch the latest user files/folders to avoid overwriting
                const userRef = doc(db, 'users', userId);
                const userDocSnap = await getDoc(userRef);
                const latestUserData = userDocSnap.data() || {};
                const currentFiles = latestUserData.files || {};
                const currentFolders = latestUserData.folders || {};

                const venueName = reportData.venueName || reportData.venue?.name || 'Report';
                const firstPhoto = (reportData.photos && reportData.photos[0]) || '';

                // Create report entry in files using new structure
                const fileId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                const reportFileData = {
                    name: `${venueName} - Report`,
                    id: fileId,
                    parentId: userUploadsFolderId,
                    itemType: 'report',
                    data: {
                        reportId: reportData.id,
                        venueId: reportData.venueId,
                        venueName,
                        url: firstPhoto,
                        imageUrl: firstPhoto,
                        hasPhotos: Array.isArray(reportData.photos) && reportData.photos.length > 0,
                        hasVideos: Array.isArray(reportData.videos) && reportData.videos.length > 0,
                        hasDocuments: Array.isArray(reportData.documents) && reportData.documents.length > 0,
                        status: reportData.status || 'pending',
                        userType: reportData.userType,
                        isTechSpecsOnly: !!reportData.isTechSpecsOnly,
                        createdAt: reportData.createdAt || Timestamp.now(),
                        dateOfPerformance: reportData.dateOfPerformance,
                        bandName: reportData.bandName,
                        datesWorked: reportData.datesWorked,
                        jobTitle: reportData.jobTitle,
                        notes: reportData.notes
                    },
                    order: Object.values(currentFiles).filter(f => f.parentId === userUploadsFolderId).length,
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now()
                };


                const updatedFiles = { ...currentFiles, [fileId]: reportFileData };

                await updateDoc(userRef, { files: updatedFiles });

                // Refresh local state with latest snapshot
                const refreshedUserDoc = await getDoc(userRef);
                const refreshedData = refreshedUserDoc.data() || {};
                setFiles(refreshedData.files || updatedFiles);
                setFolders(refreshedData.folders || currentFolders);
                setUser({ ...userData, files: refreshedData.files || updatedFiles, folders: refreshedData.folders || currentFolders });

                const targetFolderName = targetFolderId ? (currentFolders[targetFolderId]?.name || 'current folder') : 'User Uploads folder';
                Alert.alert('Report Added!', `Successfully added report for "${venueName}" to ${targetFolderName}.`);
            } catch (error) {
                console.error('Error adding report to Media Manager:', error);
                Alert.alert('Error', `Failed to add report: ${error.message}`);
            }
            
        } catch (error) {
            console.error('Error adding report to Media Manager:', error);
            Alert.alert('Error', `Failed to add report: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Add generic media (image/pdf/audio/video) to Media Manager
    const addMediaToMediaManager = async (mediaData) => {
        try {
            setLoading(true);
            const db = getFirestore();
            const auth = getAuth();
            const userId = auth.currentUser?.uid;

            if (!userId) {
                Alert.alert('Error', 'User not authenticated');
                return;
            }

            // Determine target folder (hovered folder preferred)
            const targetFolderId = await getTargetFolderForMigration();
            const destinationFolderId = targetFolderId || await getOrCreateUserUploadsFolder(userId);

            // Fetch latest user files to compute order safely
            const userRef = doc(db, 'users', userId);
            const userSnap = await getDoc(userRef);
            const latestData = userSnap.data() || {};
            const currentFiles = latestData.files || {};
            const currentFolders = latestData.folders || {};

            const name = mediaData.name || mediaData.fileName || 'media';
            const url = mediaData.url || mediaData.downloadURL || '';

            // Infer fileType from extension
            const ext = (name.split('?')[0].split('.').pop() || '').toLowerCase();
            let fileType = 'image';
            if (['pdf'].includes(ext)) fileType = 'pdf';
            else if (['mp3', 'wav', 'aac', 'flac', 'ogg', 'm4a', 'wma'].includes(ext)) fileType = 'audio';
            else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(ext)) fileType = 'video';
            else if (['doc', 'docx', 'txt', 'rtf'].includes(ext)) fileType = 'document';

            const fileId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            const newFile = {
                id: fileId,
                name,
                parentId: destinationFolderId,
                itemType: 'file',
                data: {
                    url,
                    fileType
                },
                order: Object.values(currentFiles).filter(f => f.parentId === destinationFolderId).length,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now()
            };

            const updatedFiles = { ...currentFiles, [fileId]: newFile };
            await updateDoc(userRef, { files: updatedFiles });

            const refreshed = await getDoc(userRef);
            const refreshedData = refreshed.data() || {};
            setFiles(refreshedData.files || updatedFiles);
            setFolders(refreshedData.folders || currentFolders);
            setUser({ ...userData, files: refreshedData.files || updatedFiles, folders: refreshedData.folders || currentFolders });

            const targetFolderName = targetFolderId ? (currentFolders[targetFolderId]?.name || 'current folder') : 'User Uploads folder';
            Alert.alert('Media Added!', `Added "${name}" to ${targetFolderName}.`);
        } catch (error) {
            console.error('Error adding media to Media Manager:', error);
            Alert.alert('Error', `Failed to add media: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Migrate venue media to User Uploads folder
    const migrateSavedVenueMedia = async (venueData) => {
        try {
            setLoading(true);
            const db = getFirestore();
            const auth = getAuth();
            const userId = auth.currentUser?.uid;
            
            if (!userId) {
                Alert.alert('Error', 'User not authenticated');
                return;
            }

            
            // Determine target folder
            const targetFolderId = await getTargetFolderForMigration();
            let userUploadsFolderId;
            
            if (targetFolderId) {
                // Use the current folder if it's within User Uploads hierarchy
                userUploadsFolderId = targetFolderId;
            } else {
                // Get or create User Uploads folder
                userUploadsFolderId = await getOrCreateUserUploadsFolder(userId);
            }

            const venueId = venueData.id;
            const savedVenueImage = userData.savedVenueImages ? userData.savedVenueImages[venueId] : null;
            
            if (!savedVenueImage) {
                Alert.alert('No Image', 'This saved venue has no image to migrate.');
                return;
            }
            
            try {
                // Generate filename
                const fileName = `${venueData.name}_saved_venue_photo`;
                
                // Add to target folder (unprotected)
                await addFileToUserUploads(userId, fileName, savedVenueImage, 'saved_venue_image', {
                    venueId: venueData.id,
                    venueName: venueData.name,
                    mediaType: 'photos',
                    savedVenueId: venueData.id,
                    savedVenueName: venueData.name,
                    migrationDate: new Date().toISOString(),
                    targetFolderId: userUploadsFolderId // Pass the target folder ID
                });
                
            } catch (error) {
            }
    
            
            // Update local state
            const userRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
                const userData = userDoc.data();
                setFolders(userData.folders || {});
                setFiles(userData.files || {});
            }
            
            const targetFolderName = targetFolderId ? folders[targetFolderId]?.name || 'current folder' : 'User Uploads folder';
            Alert.alert(
                'Venue Media Migrated!', 
                `Successfully migrated saved venue photo for "${venueData.name}" to ${targetFolderName}.`
            );
            
        } catch (error) {
            console.error('Error migrating venue media:', error);
            Alert.alert('Error', `Failed to migrate venue media: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Migrate report media to User Uploads folder
    const migrateReportMedia = async (reportData) => {
        try {
            setLoading(true);
            const db = getFirestore();
            const auth = getAuth();
            const userId = auth.currentUser?.uid;
            
            if (!userId) {
                Alert.alert('Error', 'User not authenticated');
                return;
            }

            
            // Determine target folder
            const targetFolderId = await getTargetFolderForMigration();
            let userUploadsFolderId;
            
            if (targetFolderId) {
                // Use the current folder if it's within User Uploads hierarchy
                userUploadsFolderId = targetFolderId;
            } else {
                // Get or create User Uploads folder
                userUploadsFolderId = await getOrCreateUserUploadsFolder(userId);
            }
            
            let migratedCount = 0;
            const mediaTypes = ['photos', 'videos', 'documents'];
            
            // Process each media type
            for (const mediaType of mediaTypes) {
                const mediaArray = reportData[mediaType] || [];
                
                for (let i = 0; i < mediaArray.length; i++) {
                    const mediaUrl = mediaArray[i];
                    if (mediaUrl && typeof mediaUrl === 'string') {
                        try {
                            // Generate filename
                            const fileName = `${reportData.venueName}_report_${mediaType.slice(0, -1)}_${i + 1}`;
                            
                            // Add to target folder (unprotected)
                            await addFileToUserUploads(userId, fileName, mediaUrl, 'report_migration', {
                                reportId: reportData.id,
                                venueId: reportData.venueId,
                                venueName: reportData.venueName,
                                mediaType: mediaType,
                                originalIndex: i,
                                migrationDate: new Date().toISOString(),
                                targetFolderId: userUploadsFolderId // Pass the target folder ID
                            });
                            
                            migratedCount++;
                        } catch (error) {
                        }
                    }
                }
            }
            
            // Update local state
            const userRef = doc(db, 'users', userId);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists()) {
                const userData = userDoc.data();
                setFolders(userData.folders || {});
                setFiles(userData.files || {});
            }
            
            const targetFolderName = targetFolderId ? folders[targetFolderId]?.name || 'current folder' : 'User Uploads folder';
            Alert.alert(
                'Report Media Migrated!', 
                `Successfully migrated ${migratedCount} media files from report for "${reportData.venueName}" to ${targetFolderName}.`
            );
            
        } catch (error) {
            console.error('Error migrating report media:', error);
            Alert.alert('Error', `Failed to migrate report media: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    // Check if migration is needed

    // Update local state when userData changes
    useEffect(() => {
        setFolders(userData?.folders || {});
        setFiles(userData?.files || {});
    }, [userData]);

    // Preserve navigation state when folders change
    useEffect(() => {
        // If we're currently in a folder that no longer exists, navigate back to root
        if (currentFolderId && !folders[currentFolderId]) {
            setCurrentFolderId(null);
            setFolderPath([]);
        }
    }, [folders, currentFolderId]);

    const db = getFirestore();
    const storage = getStorage();



    // Detect if user has a mouse/cursor (desktop) or touch (mobile)
    const hasMouseSupport = () => {
        if (Platform.OS !== 'web') return false;

        // Check if the device has a mouse
        return window.matchMedia('(pointer: fine)').matches;
    };

    // Check if device supports touch
    const hasTouchSupport = () => {
        if (Platform.OS === 'ios' || Platform.OS === 'android') return true;

        if (Platform.OS === 'web') {
            return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        }

        return false;
    };


    // Navigation helper functions
    const navigateToFolder = (folderId) => {
        if (folderId) {
            const folder = folders[folderId];
            if (folder) {
                const newFolderPath = [...folderPath, { id: folderId, name: folder.name }];
                
                // Update state
                setCurrentFolderId(folderId);
                setFolderPath(newFolderPath);
                
                // Update ref
                navigationStateRef.current = {
                    currentFolderId: folderId,
                    folderPath: newFolderPath
                };
            }
        } else {
            // Update state
            setCurrentFolderId(null);
            setFolderPath([]);
            
            // Update ref
            navigationStateRef.current = {
                currentFolderId: null,
                folderPath: []
            };
        }
    };

    const navigateBack = () => {
        if (folderPath.length > 0) {
            const newPath = [...folderPath];
            newPath.pop();
            const newCurrentFolderId = newPath.length > 0 ? newPath[newPath.length - 1].id : null;
            
            setFolderPath(newPath);
            setCurrentFolderId(newCurrentFolderId);
            
            // Update ref
            navigationStateRef.current = {
                currentFolderId: newCurrentFolderId,
                folderPath: newPath
            };
        }
    };

    const navigateToBreadcrumb = (index) => {
        if (index === -1) {
            // Navigate to root
            setCurrentFolderId(null);
            setFolderPath([]);
            
            // Update ref
            navigationStateRef.current = {
                currentFolderId: null,
                folderPath: []
            };
        } else {
            const newPath = folderPath.slice(0, index + 1);
            const newCurrentFolderId = newPath[newPath.length - 1].id;
            
            setFolderPath(newPath);
            setCurrentFolderId(newCurrentFolderId);
            
            // Update ref
            navigationStateRef.current = {
                currentFolderId: newCurrentFolderId,
                folderPath: newPath
            };
        }
    };

    // Get folders in current directory
    const getCurrentFolders = () => {
        return Object.entries(userData?.folders || {})
            .filter(([_, folder]) => folder.parentId === currentFolderId)
            .sort(([, a], [, b]) => a.order - b.order);
    };

    // Get files in current directory
    const getCurrentFiles = () => {
        return Object.entries(userData?.files || {})
            .filter(([_, file]) => file.parentId === currentFolderId)
            .sort(([, a], [, b]) => a.order - b.order);
    };

    const createFolder = async () => {
        if (!newFolder.name.trim()) {
            setShowErrorModal('Please enter a folder name');
            return;
        }

        const userRef = doc(db, 'users', userData.uid);
        const userDoc = await getDoc(userRef);
        const currentUserData = userDoc.data();

        const folderId = `folder_${Date.now()}`;
        const folderData = {
            name: newFolder.name.trim(),
            color: newFolder.color,
            parentId: currentFolderId, // Create in current folder level
            order: Object.values(currentUserData ? currentUserData.folders : {}).filter(f => f.parentId === currentFolderId).length,
            backgroundImg: newFolder.backgroundImg || '',
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
        };

        const updatedFolders = { ...folders, [folderId]: folderData };

        try {
            const userRef = doc(db, 'users', userData.uid);
            await updateDoc(userRef, { folders: updatedFolders });

            setFolders(updatedFolders);
            setUser({ ...userData, folders: updatedFolders });
            setNewFolder({ name: '', color: '#0bb5df', backgroundImg: null });
            setShowCreateFolder(false);

        } catch (error) {
            console.error('Error creating folder:', error);
            setShowErrorModal('Failed to create folder');
        }
    };

    const deleteFolder = async (folderId) => {
        const folder = folders[folderId];
        
        // Check if folder is protected
        if (folder.protected) {
            setShowErrorModal('This folder is protected and cannot be deleted');
            return;
        }
        
        if (deleteConfirmText !== folder.name) {
            setShowErrorModal('Folder name does not match');
            return;
        }

        try {
            // Get all folders and files that need to be deleted (recursively)
            const { foldersToDelete, filesToDelete } = getAllNestedItems(folderId);
            
            // Delete all files from storage (with smart deletion logic)
            let storageDeleteCount = 0;
            let storageSkipCount = 0;
            
            for (const [fileId, file] of filesToDelete) {
                try {
                    // Only try to delete from storage if it's a file we actually uploaded to our storage
                    if (file.storagePath && 
                        file.storagePath.startsWith('userMedia/') && 
                        !file.migratedFrom && 
                        !file.uploadSource) {
                        
                        const storageRef = ref(storage, file.storagePath);
                        await deleteObject(storageRef);
                        storageDeleteCount++;
                    } else {
                        storageSkipCount++;
                    }
                } catch (error) {
                    console.warn(`⚠️ Could not delete file ${file.name} from storage:`, error);
                    storageSkipCount++;
                }
            }

            // Remove all folders and files from database
            const updatedFolders = { ...folders };
            const updatedFiles = { ...files };

            // Delete all nested folders
            foldersToDelete.forEach(([folderId]) => {
                delete updatedFolders[folderId];
            });

            // Delete all files
            filesToDelete.forEach(([fileId]) => {
                delete updatedFiles[fileId];
            });

            // Update database
            const userRef = doc(db, 'users', userData.uid);
            await updateDoc(userRef, {
                folders: updatedFolders,
                files: updatedFiles
            });

            // Update local state
            setFolders(updatedFolders);
            setFiles(updatedFiles);
            setUser({ ...userData, folders: updatedFolders, files: updatedFiles });
            setShowDeleteConfirm(null);
            setDeleteConfirmText('');

            // Show success message
            const summary = 
                `Successfully deleted folder "${folder.name}" and all its contents:\n\n` +
                `• ${foldersToDelete.length} folders deleted\n` +
                `• ${filesToDelete.length} files removed\n` +
                `• ${storageDeleteCount} files deleted from storage\n` +
                `• ${storageSkipCount} files skipped (external/migrated)`;
            
            setShowSuccessModal(summary);

        } catch (error) {
            console.error('❌ Error deleting folder:', error);
            setShowErrorModal(`Failed to delete folder: ${error.message}`);
        }
    };

    // Helper function to recursively get all nested folders and files
    const getAllNestedItems = (parentFolderId) => {
        const foldersToDelete = [];
        const filesToDelete = [];
        
        // Function to recursively collect items
        const collectItems = (folderId) => {
            // Add current folder to deletion list
            if (folders[folderId]) {
                foldersToDelete.push([folderId, folders[folderId]]);
            }
            
            // Get all files in current folder
            const folderFiles = Object.entries(files).filter(([_, file]) => file.parentId === folderId);
            filesToDelete.push(...folderFiles);
            
            // Get all subfolders and recursively collect their items
            const subfolders = Object.entries(folders).filter(([_, folder]) => folder.parentId === folderId);
            subfolders.forEach(([subfolderId]) => {
                collectItems(subfolderId);
            });
        };
        
        // Start collection from the target folder
        collectItems(parentFolderId);
        
        return { foldersToDelete, filesToDelete };
    };

    const uploadFileToFolder = async (folderId) => {
        try {
            setLoading(true);
            const result = await DocumentPicker.getDocumentAsync({
                type: [
                    'image/*',
                    'video/mp4',
                    'audio/mp3',
                    'application/pdf',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'text/plain',
                    'audio/*',
                    'video/*'
                ],
                multiple: true
            });

            if (!result.canceled && result.assets && result.assets.length > 0) {
                const uploadPromises = result.assets.map(async (file) => {
                    const fileUri = file.uri;
                    const response = await fetch(fileUri);
                    const fileBlob = await response.blob();

                    const fileExt = file.name.split('.').pop();
                    const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
                    const storagePath = `userMedia/${userData.uid}/${folderId}/${fileName}`;
                    const storageRef = ref(storage, storagePath);

                    await uploadBytes(storageRef, fileBlob);
                    const downloadURL = await getDownloadURL(storageRef);

                    const fileId = `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                    const fileExtLower = fileExt.toLowerCase();
                    let fileType = 'image';
                    
                    // Determine file type based on extension
                    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(fileExtLower)) {
                        fileType = 'image';
                    } else if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(fileExtLower)) {
                        fileType = 'video';
                    } else if (['mp3', 'wav', 'aac', 'flac', 'ogg', 'm4a', 'wma'].includes(fileExtLower)) {
                        fileType = 'audio';
                    } else if (['pdf', 'doc', 'docx', 'txt', 'rtf'].includes(fileExtLower)) {
                        fileType = 'document';
                    }
                    
                    return {
                        [fileId]: {
                            name: file.name,
                            id: fileId,
                            parentId: folderId,
                            itemType: 'file',
                            data: {
                                fileType: fileType,
                                url: downloadURL,
                                manualUpload: true
                            },
                            order: Object.values(files).filter(f => f.parentId === folderId).length,
                            createdAt: Timestamp.now(),
                            updatedAt: Timestamp.now()
                        }
                    };
                });

                const uploadedFiles = await Promise.all(uploadPromises);
                const newFiles = uploadedFiles.reduce((acc, file) => ({ ...acc, ...file }), {});
                const updatedFiles = { ...files, ...newFiles };

                const userRef = doc(db, 'users', userData.uid);
                await updateDoc(userRef, { files: updatedFiles });

                setFiles(updatedFiles);

                setUser({ ...userData, files: updatedFiles });

                Alert.alert('Success', `${uploadedFiles.length} file(s) uploaded successfully!`);
            }
        } catch (error) {
            console.error('Error uploading files:', error);
            Alert.alert('Error', 'Failed to upload files');
        } finally {
            setLoading(false);
        }
    };

    const uploadFolderBackground = async (folderId) => {
        try {
            const result = await DocumentPicker.getDocumentAsync({ type: 'image/*' });

            if (!result.canceled && result.assets && result.assets.length > 0) {
                const file = result.assets[0];
                const fileUri = file.uri;
                const response = await fetch(fileUri);
                const fileBlob = await response.blob();

                const fileExt = file.name.split('.').pop();
                const fileName = `folder_bg_${folderId}_${Date.now()}.${fileExt}`;
                const storagePath = `userMedia/${userData.uid}/folder_backgrounds/${fileName}`;
                const storageRef = ref(storage, storagePath);

                await uploadBytes(storageRef, fileBlob);
                const downloadURL = await getDownloadURL(storageRef);

                const updatedFolders = {
                    ...folders,
                    [folderId]: {
                        ...folders[folderId],
                        backgroundImg: downloadURL,
                        updatedAt: Timestamp.now()
                    }
                };

                const userRef = doc(db, 'users', userData.uid);
                await updateDoc(userRef, { folders: updatedFolders });

                setFolders(updatedFolders);
                setUser({ ...userData, folders: updatedFolders });
                // setUser({ ...userData, folders: updatedFolders });
            }
        } catch (error) {
            console.error('Error uploading folder background:', error);
            Alert.alert('Error', 'Failed to upload folder background');
        }
    };

    const deleteFile = async (fileId) => {
        try {
            const folderProtected = folders[files[fileId].parentId].protected;
            
            const file = files[fileId];
            if (!file) {
                setShowErrorModal('File not found');
                return;
            }
            
            // Only try to delete from storage if it's a file we actually uploaded to our storage
            // Skip storage deletion for migrated files or files from external sources
            if (file.storagePath && 
                file.storagePath.startsWith('userMedia/') && 
                !folderProtected) {
                
                try {
                    const storageRef = ref(storage, file.storagePath);
                    await deleteObject(storageRef);
                } catch (storageError) {
                    console.warn('⚠️ Could not delete file from storage (file may not exist):', storageError);
                    // Continue with database deletion even if storage deletion fails
                }
            }

            // Always remove from database
            const updatedFiles = { ...files };
            delete updatedFiles[fileId];

            const userRef = doc(db, 'users', userData.uid);
            await updateDoc(userRef, { files: updatedFiles });
            setFiles(updatedFiles);
            setUser({ ...userData, files: updatedFiles });

            setShowSuccessModal(`File "${file.name}" has been deleted from your folder system.`);

        } catch (error) {
            console.error('❌ Error deleting file:', error);
            setShowErrorModal(`Failed to delete file: ${error.message}\n\nCheck the console for more details.`);
        }
    };

    // Rename functionality
    const renameItem = async (itemId, itemType, newName) => {
        if (!newName.trim()) {
            setShowErrorModal('Please enter a valid name');
            return;
        }

        try {
            if (itemType === 'folder') {
                const updatedFolders = {
                    ...folders,
                    [itemId]: {
                        ...folders[itemId],
                        name: newName.trim(),
                        updatedAt: Timestamp.now()
                    }
                };

                const userRef = doc(db, 'users', userData.uid);
                await updateDoc(userRef, { folders: updatedFolders });
                setFolders(updatedFolders);
                setUser({ ...userData, folders: updatedFolders });
            } else {
                const updatedFiles = {
                    ...files,
                    [itemId]: {
                        ...files[itemId],
                        name: newName.trim(),
                        updatedAt: Timestamp.now()
                    }
                };

                const userRef = doc(db, 'users', userData.uid);
                await updateDoc(userRef, { files: updatedFiles });
                setFiles(updatedFiles);
                setUser({ ...userData, files: updatedFiles });
            }

            setShowRenameModal(null);
            setRenameText('');
        } catch (error) {
            console.error('Error renaming item:', error);
            setShowErrorModal('Failed to rename item');
        }
    };

    // Change folder color functionality
    const changeFolderColor = async (folderId, newColor) => {
        try {
            const updatedFolders = {
                ...folders,
                [folderId]: {
                    ...folders[folderId],
                    color: newColor,
                    updatedAt: Timestamp.now()
                }
            };

            const userRef = doc(db, 'users', userData.uid);
            await updateDoc(userRef, { folders: updatedFolders });
            setFolders(updatedFolders);
            setUser({ ...userData, folders: updatedFolders });
            setShowColorModal(null);
        } catch (error) {
            console.error('Error changing folder color:', error);
            setShowErrorModal('Failed to change folder color');
        }
    };

    const renderFolder = (folderId, folder, index) => {
        // Get subfolders and files count
        const subfolders = Object.values(folders).filter(f => f.parentId === folderId);
        const folderFiles = Object.entries(files).filter(([_, file]) => file.parentId === folderId);
        const totalItems = subfolders.length + folderFiles.length;

        const isSelected = selectedItem && selectedItem.id === folderId;
        
        // Check if folder has matching files for search indicator
        const hasMatchingFiles = searchQuery.trim() && folderFiles.some(([_, file]) => 
            file.name.toLowerCase().includes(searchQuery.toLowerCase())
        );

        return (
            <View
                key={folderId}
                style={mediaManagerStyles.folderContainer}>
                <div
                    style={mediaManagerStyles.folderWrapper}
                    onMouseEnter={() => {
                        setHoverDropFolderId(folderId);
                        hoverDropFolderRef.current = folderId;
                    }}
                    onMouseLeave={() => {
                        setHoverDropFolderId(null);
                        hoverDropFolderRef.current = null;
                    }}
                    {...(hasMouseSupport() && Platform.OS === 'web' && {
                        onContextMenu: (e) => {
                            e.preventDefault();
                            const { clientX, clientY } = e.nativeEvent;
                            // Only show context menu for non-protected folders
                            if (!folder.protected) {
                                setContextMenu({
                                    item: { id: folderId, type: 'folder', item: folder },
                                    position: { x: clientX, y: clientY }
                                });
                            }
                        }
                    })}
                >
                    <TouchableOpacity
                        onPress={() => handleItemPress(folderId, 'folder', folder, () => {
                            navigateToFolder(folderId);
                            setSelectedItem(null);
                        })}
                        onLongPress={(e) => {
                            // Long press for touch devices (mobile/tablet)
                            if (hasTouchSupport()) {
                                const { pageX, pageY } = e.nativeEvent;
                            // Only show context menu for non-protected folders
                                if (!folder.protected) {
                                    setContextMenu({
                                        item: { id: folderId, type: 'folder', item: folder },
                                        position: { x: pageX, y: pageY }
                                    });
                                }
                            }
                        }}
                        style={[
                            mediaManagerStyles.folderTouchable,
                            isSelected && mediaManagerStyles.folderTouchableSelected
                        ]}>
                        {/* Folder Tab (protruding part) */}
                        <View style={[
                            mediaManagerStyles.folderTab,
                            { backgroundColor: folder.color }
                        ]} />
                        
                        {/* Search match indicator */}
                        {hasMatchingFiles && (
                            <View style={mediaManagerStyles.searchMatchIndicator}>
                                <Ionicons name="search" size={10} color="#ffffff" />
                            </View>
                        )}

                        {/* Main folder body */}
                        <View style={[
                            mediaManagerStyles.folderBody,
                            { backgroundColor: folder.color }
                        ]}>
                            {/* Background image if exists */}
                            {folder.backgroundImg && (
                                <Image
                                    source={{ uri: folder.backgroundImg }}
                                    style={mediaManagerStyles.folderBackgroundImage}
                                    resizeMode="cover"
                                />
                            )}

                            {/* Folder gradient overlay */}
                            <LinearGradient
                                colors={[folder.color, folder.color + 'CC']}
                                style={mediaManagerStyles.folderGradient}
                            >
                                {folder.backgroundImg && (
                                    <Image 
                                        source={{ uri: folder.backgroundImg }} 
                                        style={mediaManagerStyles.folderGradientImage} 
                                        resizeMode="cover" 
                                    />
                                )}
                            </LinearGradient>
                        </View>


                    </TouchableOpacity>

                    {/* Folder name and info */}
                    <View style={mediaManagerStyles.folderInfo}>
                        <View style={mediaManagerStyles.folderInfoRow}>
                            <Text style={mediaManagerStyles.folderName} numberOfLines={1}>{folder.name}</Text>
                            {folder.protected && (
                                <Ionicons name="shield-checkmark" size={12} color="#2ecc71" />
                            )}
                        </View>
                        <Text style={mediaManagerStyles.folderDetails}>
                            {totalItems} items{folder.protected ? ' • Protected' : ''}
                            {hasMatchingFiles ? ' • Contains matches' : ''}
                        </Text>
                    </View>
                </div>
            </View>
        );
    };

    const renderItem = (itemId, item, index) => {
        const isSelected = selectedItem && selectedItem.id === itemId;
        const itemType = item.itemType;

        const handleItemPressWrapper = () => {
            handleItemPress(itemId, 'item', item, () => {
                // Double tap: Open in MediaViewer
                const mediaArray = getCurrentFolderMedia();
                const currentIndex = mediaArray.findIndex(media => media.id === itemId);
                if (currentIndex !== -1) {
                    setMediaViewerMedia(mediaArray);
                    setSelectedMediaIndex(currentIndex);
                    setIsMediaViewerVisible(true);
                }
                setSelectedItem(null);
            });
        };

        const handleLongPress = (e) => {
            if (hasTouchSupport()) {
                const { pageX, pageY } = e.nativeEvent;
                setContextMenu({
                    item: { id: itemId, type: 'file', item: item },
                    position: { x: pageX, y: pageY }
                });
            }
        };

        // Handle different item types
        if (itemType === 'savedVenue') {
            return (
                <ItemWrapper
                    key={itemId}
                    isSelected={isSelected}
                    onPress={handleItemPressWrapper}
                    onLongPress={handleLongPress}
                    itemType="savedVenue"
                >
                    <VenueCardFile
                        venue={{
                            ...item.data,
                            imageUrl: item.data?.imageUrl || item.data?.url
                        }}
                        isSelected={isSelected}
                        onPress={() => {}} 
                        onLongPress={() => {}} 
                        onView={onViewVenue}
                        onDeletePhoto={onDeleteVenuePhoto}
                        onAddPhoto={onAddVenuePhoto}
                        userData={userData}
                    />
                </ItemWrapper>
            );
        } 
        else if (itemType === 'report') {
            return (
                <ItemWrapper
                    key={itemId}
                    isSelected={isSelected}
                    onPress={handleItemPressWrapper}
                    onLongPress={handleLongPress}
                    itemType="report"
                >
                    <ReportCardFile
                        report={item.data}
                        isSelected={isSelected}
                        onPress={() => {}} 
                        onLongPress={() => {}} 
                        onEdit={onEditReport}
                        onDelete={onDeleteReport}
                    />
                </ItemWrapper>
            );
        } 
        else {
            // Handle file type
            return (
                <View key={itemId} style={mediaManagerStyles.folderContainer}>
                    <ItemWrapper
                        isSelected={isSelected}
                        onPress={handleItemPressWrapper}
                        onLongPress={handleLongPress}
                        itemType="file"
                    >
                        {renderFileContent(itemId, item, index)}
                    </ItemWrapper>
                    <View style={mediaManagerStyles.folderInfo}>
                        <View style={mediaManagerStyles.folderInfoRow}>
                            <Text
                                style={mediaManagerStyles.folderName}
                                numberOfLines={1}
                                ellipsizeMode="middle"
                                onPress={() => {
                                    setRenameText(item.name || '');
                                    setShowRenameModal({ id: itemId, type: 'file', item });
                                }}
                            >
                                {item.name || 'Untitled'}
                            </Text>
                        </View>
                    </View>
                </View>
            );
        }
    };

    const renderFileContent = (fileId, file, index) => {
        const fileType = getFileType(file);
        const fileUrl = file.data?.url || file.url;
        
        // If it's an image, show the actual image
        if (fileType === 'image' || fileType === 'photos') {
            return (
                <Image
                    source={{ uri: fileUrl }}
                    style={mediaManagerStyles.fileImage}
                    onError={() => {
                        // If image fails to load, it might be a non-image file
                    }}
                />
            );
        }
        
        // Otherwise, show appropriate placeholder based on file type
        if (fileType === 'pdf' || fileType === 'documents') {
            return (
                <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.pdfBackground]}>
                    <Ionicons name="document-text" size={32} color="#ffffff" />
                </View>
            );
        }
        
        if (fileType === 'document') {
            return (
                <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.documentBackground]}>
                    <Ionicons name="document" size={32} color="#ffffff" />
                    <Text style={mediaManagerStyles.filePlaceholderText} numberOfLines={2} ellipsizeMode="middle">
                        {file.name}
                    </Text>
                </View>
            );
        }
        
        if (fileType === 'audio') {
            return (
                <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.audioBackground]}>
                    <Ionicons name="musical-notes" size={32} color="#ffffff" />
                    <Text style={mediaManagerStyles.filePlaceholderText} numberOfLines={2} ellipsizeMode="middle">
                        {file.name}
                    </Text>
                </View>
            );
        }
        
        if (fileType === 'video') {
            return (
                <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.videoBackground]}>
                    <Ionicons name="play-circle" size={32} color="#ffffff" />
                    <Text style={mediaManagerStyles.filePlaceholderText} numberOfLines={2} ellipsizeMode="middle">
                        {file.name}
                    </Text>
                </View>
            );
        }
        
        // Default fallback
        return (
            <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.defaultBackground]}>
                <Ionicons name="document" size={32} color="#ffffff" />
                <Text style={mediaManagerStyles.filePlaceholderText} numberOfLines={2} ellipsizeMode="middle">
                    {file.name}
                </Text>
            </View>
        );
    };

    const renderFile = (fileId, file, index) => {
        const isSelected = selectedItem && selectedItem.id === fileId;

        return (
            <View
                key={fileId}
                style={mediaManagerStyles.legacyFileContainer}>
                <div
                    {...(hasMouseSupport() && {
                        onContextMenu: (e) => {
                            // Right-click for desktop with mouse
                            e.preventDefault();
                            const { clientX, clientY } = e.nativeEvent;
                            // Allow context menu for all files (protected status removed for files)
                            setContextMenu({
                                    item: { id: fileId, type: 'file', item: file },
                                    position: { x: clientX, y: clientY }
                                });
                        }
                    })}
                    style={mediaManagerStyles.legacyFileWrapper}
                >
                    <TouchableOpacity
                        key={fileId}
                        onPress={() => handleItemPress(fileId, 'file', file, () => {
                            // Double tap: Handle different file types
                            if (file.url) {
                                const fileType = getFileType(file);
                                
                                // For all files with URLs, open in MediaViewer
                                const folderImages = getCurrentFolderImages();
                                const imageUrls = Object.values(folderImages);
                                const currentIndex = imageUrls.findIndex(url => url === file.url);
                                
                                if (currentIndex !== -1) {
                                    // For images found in folder images, use existing logic
                                    handleMediaPress(currentIndex);
                                } else {
                                    // For any file not found in folder images, create a single-item media array
                                    const singleMediaArray = [{
                                        id: fileId,
                                        name: file.name,
                                        url: file.url,
                                        type: fileType
                                    }];
                                    
                                    // Set the media and open MediaViewer
                                    setMediaViewerMedia(singleMediaArray);
                                    setSelectedMediaIndex(0);
                                    setIsMediaViewerVisible(true);
                                }
                            } else {
                                setShowSuccessModal(`Opening ${file.name}`);
                            }
                            setSelectedItem(null);
                        })}
                        onLongPress={(e) => {
                            // Long press for touch devices (mobile/tablet)
                            if (hasTouchSupport()) {
                                const { pageX, pageY } = e.nativeEvent;
                            // Allow context menu for all files (protected status removed for files)
                                    setContextMenu({
                                        item: { id: fileId, type: 'file', item: file },
                                        position: { x: pageX, y: pageY }
                                    });
                            }
                        }}
                        style={[
                            mediaManagerStyles.legacyFileTouchable,
                            isSelected && mediaManagerStyles.legacyFileTouchableSelected
                        ]}>
                        {(() => {
                            const fileType = getFileType(file);
                            
                            // If it's an image, show the actual image
                            if (fileType === 'image' || fileType === 'photos') {
                                return (
                        <Image
                            source={{ uri: file.url }}
                            style={mediaManagerStyles.fileImage}
                                        onError={() => {
                                            // If image fails to load, it might be a non-image file
                                        }}
                                    />
                                );
                            }
                            
                            // Otherwise, show appropriate placeholder based on file type
                            if (fileType === 'pdf' || fileType === 'documents') {
                                return (
                                    <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.pdfBackground]}>
                                        <Ionicons name="document-text" size={32} color="#ffffff" />
                                    </View>
                                );
                            }
                            
                            if (fileType === 'document') {
                                return (
                                    <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.documentBackground]}>
                                        <Ionicons name="document" size={32} color="#ffffff" />
                                        <Text style={mediaManagerStyles.filePlaceholderText} numberOfLines={2} ellipsizeMode="middle">
                                            {file.name}
                                        </Text>
                                    </View>
                                );
                            }
                            
                            if (fileType === 'audio') {
                                return (
                                    <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.audioBackground]}>
                                        <Ionicons name="musical-notes" size={32} color="#ffffff" />
                                        <Text style={mediaManagerStyles.filePlaceholderText} numberOfLines={2} ellipsizeMode="middle">
                                            {file.name}
                                        </Text>
                                    </View>
                                );
                            }
                            
                            if (fileType === 'video') {
                                return (
                                    <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.videoBackground]}>
                                        <Ionicons name="play-circle" size={32} color="#ffffff" />
                                        <Text style={mediaManagerStyles.filePlaceholderText} numberOfLines={2} ellipsizeMode="middle">
                                            {file.name}
                                        </Text>
                                    </View>
                                );
                            }
                            
                            // Default fallback
                            return (
                                <View style={[mediaManagerStyles.filePlaceholder, mediaManagerStyles.defaultBackground]}>
                                    <Ionicons name="attach" size={32} color="#ffffff" />
                                    <Text style={mediaManagerStyles.filePlaceholderText} numberOfLines={2} ellipsizeMode="middle">
                                        {file.name}
                                    </Text>
                                </View>
                            );
                        })()}



                        <LinearGradient
                            colors={['rgba(0,0,0,0)', 'rgba(0,0,0,0.5)']}
                            style={mediaManagerStyles.legacyFileGradient}
                        >
                            <Text style={mediaManagerStyles.legacyFileName} numberOfLines={1}>{file.name}</Text>
                        </LinearGradient>
                    </TouchableOpacity>
                </div>
            </View>

        );
    };

    // Context menu component
    const renderContextMenu = () => {
        if (!contextMenu) return null;

        const { item, position } = contextMenu;

        return (
            <Modal
                visible={true}
                transparent={true}
                animationType="none"
                onRequestClose={() => setContextMenu(null)}
            >
                <TouchableOpacity
                    style={{ flex: 1 }}
                    activeOpacity={1}
                    onPress={() => setContextMenu(null)}
                >
                    <View style={[
                        mediaManagerStyles.contextMenu,
                        {
                            left: Math.min(position.x, window.innerWidth - 150), // Ensure menu doesn't go off-screen
                            top: Math.min(position.y, window.innerHeight - 200), // Ensure menu doesn't go off-screen
                        }
                    ]}>
                        {/* Add background image option (folders only) */}
                        {item.type === 'folder' && (
                            <TouchableOpacity
                                onPress={() => {
                                    uploadFolderBackground(item.id);
                                    setContextMenu(null);
                                }}
                                style={mediaManagerStyles.contextMenuItem}
                            >
                                <Ionicons name="image" size={16} color="#333" />
                                <Text style={mediaManagerStyles.contextMenuText}>Add Background</Text>
                            </TouchableOpacity>
                        )}

                        {/* Edit color option (folders only) */}
                        {item.type === 'folder' && (
                            <TouchableOpacity
                                onPress={() => {
                                    setSelectedColor(item.item.color || '#0bb5df');
                                    setShowColorModal(item);
                                    setContextMenu(null);
                                }}
                                style={mediaManagerStyles.contextMenuItem}
                            >
                                <Ionicons name="color-palette" size={16} color="#333" />
                                <Text style={mediaManagerStyles.contextMenuText}>Edit Color</Text>
                            </TouchableOpacity>
                        )}

                        {/* Rename option - hide for venues and reports */}
                        {!(item.type === 'item' && (item.item.itemType === 'savedVenue' || item.item.itemType === 'report')) && (
                            <TouchableOpacity
                                onPress={() => {
                                    setRenameText(item.item.name || '');
                                    setShowRenameModal(item);
                                    setContextMenu(null);
                                }}
                                style={mediaManagerStyles.contextMenuItem}
                            >
                                <Ionicons name="create" size={16} color="#333" />
                                <Text style={mediaManagerStyles.contextMenuText}>Rename</Text>
                            </TouchableOpacity>
                        )}

                        {/* Delete option - hide only for protected folders, allow file deletion in protected folders */}
                        {!(item.type === 'folder' && item.item.protected) && (
                            <TouchableOpacity
                                onPress={() => {
                                    if (item.type === 'folder') {
                                        setDeleteConfirmText('');
                                        setShowDeleteConfirm(item.id);
                                    } else {
                                        setShowDeleteFileModal(item);
                                    }
                                    setContextMenu(null);
                                }}
                                style={[mediaManagerStyles.contextMenuItemLast, mediaManagerStyles.contextMenuItem]}
                            >
                                <Ionicons name="trash" size={16} color="#e74c3c" />
                                <Text style={mediaManagerStyles.contextMenuTextDanger}>Delete</Text>
                            </TouchableOpacity>
                        )}
                    </View>
                </TouchableOpacity>
            </Modal>
        );
    };

    // First row: Breadcrumb and Search
    const renderBreadcrumbRow = () => {
        const isNarrow = Platform.OS === 'web' && typeof window !== 'undefined' && window.innerWidth <= 1024;
        return (
                <View style={[mediaManagerStyles.breadcrumbRow, isNarrow && mediaManagerStyles.breadcrumbRowMobile]}>
                    {/* Breadcrumb */}
                    <View style={mediaManagerStyles.breadcrumbContainer}>
                    {/* Back button */}
                    {folderPath.length > 0 && (
                        <TouchableOpacity
                            onPress={navigateBack}
                            style={mediaManagerStyles.backButton}
                        >
                            <Ionicons name="arrow-back" size={16} color="#ffffff" />
                        </TouchableOpacity>
                    )}

                    {/* Breadcrumb path */}
                    <View style={mediaManagerStyles.breadcrumbPath}>
                        <TouchableOpacity onPress={() => navigateToBreadcrumb(-1)}>
                            <Text style={mediaManagerStyles.breadcrumbRoot}>Root</Text>
                        </TouchableOpacity>

                        {folderPath.map((pathItem, index) => (
                            <View key={pathItem.id} style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <Text style={mediaManagerStyles.breadcrumbSeparator}>/</Text>
                                <TouchableOpacity onPress={() => navigateToBreadcrumb(index)}>
                                    <Text style={[
                                        mediaManagerStyles.breadcrumbItem,
                                        index === folderPath.length - 1 
                                            ? mediaManagerStyles.breadcrumbItemActive 
                                            : mediaManagerStyles.breadcrumbItemInactive
                                    ]}>{pathItem.name}</Text>
                                </TouchableOpacity>
                            </View>
                        ))}
                    </View>
                </View>

                    {/* Search Bar */}
                    <View style={[mediaManagerStyles.searchContainer, isNarrow && mediaManagerStyles.searchContainerFullWidth]}>
                    <Ionicons name="search" size={16} color="#ffffff" style={{ marginRight: 8 }} />
                    <TextInput
                        placeholder="Search files and folders..."
                        placeholderTextColor="rgba(255,255,255,0.6)"
                        value={searchQuery}
                        onChangeText={setSearchQuery}
                        style={mediaManagerStyles.searchInput}
                    />
                    {searchQuery.length > 0 && (
                        <TouchableOpacity
                            onPress={() => setSearchQuery('')}
                            style={mediaManagerStyles.searchClearButton}
                        >
                            <Ionicons name="close-circle" size={16} color="rgba(255,255,255,0.6)" />
                        </TouchableOpacity>
                    )}
                </View>
            </View>
        );
    };

    // Second row: Action buttons
    const renderActionButtonsRow = () => {
        let currentIndex = null;
        if (selectedItem) {
            currentIndex = filteredItems.findIndex(item => item.id === selectedItem.id);
        }
        const canMoveUp = currentIndex > 0;
        const canMoveDown = currentIndex < filteredItems.length - 1;
        
        return (
                <View style={mediaManagerStyles.actionButtonsRow}>
                    {/* <TouchableOpacity
                        onPress={migrateExistingMedia}
                        disabled={loading}
                        style={[
                            mediaManagerStyles.actionButton,
                            mediaManagerStyles.migrateButton,
                            loading && mediaManagerStyles.actionButtonDisabled
                        ]}
                    >
                        <Ionicons name={loading ? "hourglass" : "cloud-upload"} size={16} color="#ffffff" />
                        <Text style={mediaManagerStyles.actionButtonText}>
                        {loading ? 'MIGRATING...' : 'MIGRATE MEDIA'}
                        </Text>
                    </TouchableOpacity> */}

                    {/* <TouchableOpacity
                        onPress={resetAllMediaData}
                        disabled={loading}
                        style={[
                            mediaManagerStyles.actionButton,
                            mediaManagerStyles.resetButton,
                            loading && mediaManagerStyles.actionButtonDisabled
                        ]}
                    >
                        <Ionicons name={loading ? "hourglass" : "trash"} size={16} color="#ffffff" />
                        <Text style={mediaManagerStyles.actionButtonText}>
                            {loading ? 'RESETTING...' : 'RESET ALL'}
                        </Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                        onPress={setAllFilesUnprotected}
                        disabled={loading}
                        style={[
                            mediaManagerStyles.actionButton,
                            mediaManagerStyles.unprotectButton,
                            loading && mediaManagerStyles.actionButtonDisabled
                        ]}
                    >
                        <Text style={mediaManagerStyles.actionButtonText}>
                            {loading ? 'UPDATING...' : 'UNPROTECT FILES'}
                        </Text>
                    </TouchableOpacity> */}

                {/* Open button */}
                    <TouchableOpacity
                        onPress={() => {
                            if (selectedItem && selectedItem.type === 'folder') {
                                navigateToFolder(selectedItem.id);
                            } 
                            setSelectedItem(null);
                        }}
                        style={mediaManagerStyles.openButton}
                    >
                        <Text style={mediaManagerStyles.actionButtonText}>OPEN</Text>
                    </TouchableOpacity>

                    {/* Move up button */}
                    <TouchableOpacity
                        onPress={() => {
                        if (selectedItem && canMoveUp) {
                                moveItem(selectedItem.id, selectedItem.type, 'up');
                                setSelectedItem(null);
                            }
                        }}
                        style={[
                            mediaManagerStyles.moveButton,
                            (!selectedItem || !canMoveUp) && mediaManagerStyles.moveButtonDisabled
                        ]}
                    >
                        <Ionicons name="chevron-up" size={16} color="#ffffff" />
                    </TouchableOpacity>

                    {/* Move down button */}
                    <TouchableOpacity
                        onPress={() => {
                        if (selectedItem && canMoveDown) {
                                moveItem(selectedItem.id, selectedItem.type, 'down');
                                setSelectedItem(null);
                            }
                        }}
                        style={[
                            mediaManagerStyles.moveButton,
                            (!selectedItem || !canMoveDown) && mediaManagerStyles.moveButtonDisabled
                        ]}
                    >
                        <Ionicons name="chevron-down" size={16} color="#ffffff" />
                    </TouchableOpacity>

                    {/* Clear selection */}
                    <TouchableOpacity
                        onPress={() => setSelectedItem(null)}
                        style={[
                            mediaManagerStyles.clearButton,
                            !selectedItem && mediaManagerStyles.clearButtonDisabled
                        ]}
                    >
                        <Ionicons name="close" size={16} color="#ffffff" />
                    </TouchableOpacity>

                {/* Rename */}
                    <TouchableOpacity
                        onPress={() => {
                            if (selectedItem) {
                                const displayName = selectedItem.item?.name || '';
                                setRenameText(displayName);
                                setShowRenameModal({ id: selectedItem.id, type: selectedItem.type === 'folder' ? 'folder' : 'file', item: selectedItem.item });
                            }
                        }}
                        style={[
                            mediaManagerStyles.actionButton,
                            mediaManagerStyles.renameButton,
                            !selectedItem && mediaManagerStyles.actionButtonDisabled
                        ]}
                    >
                        <Ionicons name="create" size={14} color="#ffffff" />
                        <Text style={mediaManagerStyles.actionButtonText}>Rename</Text>
                    </TouchableOpacity>

                {/* Delete */}
                    <TouchableOpacity
                        onPress={() => {
                            if (selectedItem) {
                                setShowDeleteFileModal({ id: selectedItem.id, item: selectedItem.item });
                            }
                        }}
                        style={[
                            mediaManagerStyles.actionButton,
                            mediaManagerStyles.deleteButton,
                            !selectedItem && mediaManagerStyles.actionButtonDisabled
                        ]}
                    >
                        <Ionicons name="trash" size={14} color="#ffffff" />
                        <Text style={mediaManagerStyles.actionButtonText}>Delete</Text>
                    </TouchableOpacity>

                {/* New Folder button */}
                    <TouchableOpacity
                        onPress={() => {
                                setShowCreateFolder(true);
                        }}
                        style={[
                            mediaManagerStyles.actionButton,
                            mediaManagerStyles.newFolderButton
                        ]}
                    >
                        <Ionicons name="folder" size={16} color="#ffffff" />
                        <Text style={mediaManagerStyles.actionButtonText}>New Folder</Text>
                    </TouchableOpacity>

                {/* Add Files button */}
                    <TouchableOpacity
                        onPress={() => {
                                uploadFileToFolder(currentFolderId);
                        }}
                        disabled={loading}
                        style={[
                            mediaManagerStyles.addFilesButton,
                            loading && mediaManagerStyles.actionButtonDisabled
                        ]}
                    >
                        <Ionicons name={loading ? "hourglass" : "add"} size={14} color="#ffffff" />
                        <Text style={mediaManagerStyles.actionButtonText}>
                            {loading ? 'Adding...' : 'Add Files'}
                        </Text>
                    </TouchableOpacity>
            </View>
        );
    };

    // Get current directory contents sorted by order
    const currentFolders = getCurrentFolders();
    const currentFiles = getCurrentFiles();

    // Combine folders and files, then sort by order
    const allItems = [
        ...currentFolders.map(([id, item]) => ({ id, item, type: 'folder' })),
        ...currentFiles.map(([id, item]) => ({ id, item, type: 'file' }))
    ].sort((a, b) => a.item.order - b.item.order);

    // Filter items based on search query
    const filteredItems = searchQuery.trim() 
        ? allItems.filter(({ id, item, type }) => {
            const searchTerm = searchQuery.toLowerCase();
            
            // Check if the item name matches
            if (item.name.toLowerCase().includes(searchTerm)) {
                return true;
            }
            
            // For folders, also check if any files inside match the search term
            if (type === 'folder') {
                const folderFiles = Object.entries(files).filter(([_, file]) => file.folderId === id);
                return folderFiles.some(([_, file]) => 
                    file.name.toLowerCase().includes(searchTerm)
                );
            }
            
            return false;
          })
        : allItems;

    // Function to move item up or down
    const moveItem = async (itemId, itemType, direction) => {
        const currentIndex = filteredItems.findIndex(item => item.id === itemId);
        if (currentIndex === -1) return;

        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (newIndex < 0 || newIndex >= filteredItems.length) return;

        try {
            // Create new array with swapped positions
            const newAllItems = [...allItems];
            const currentItem = filteredItems[currentIndex];
            const targetItem = filteredItems[newIndex];
            
            // Find the actual indices in allItems
            const currentActualIndex = allItems.findIndex(item => item.id === currentItem.id);
            const targetActualIndex = allItems.findIndex(item => item.id === targetItem.id);
            
            [newAllItems[currentActualIndex], newAllItems[targetActualIndex]] = [newAllItems[targetActualIndex], newAllItems[currentActualIndex]];

            // Update order values
            const updatedFolders = { ...folders };
            const updatedFiles = { ...files };

            newAllItems.forEach((item, index) => {
                if (item.type === 'folder') {
                    updatedFolders[item.id] = { ...item.item, order: index };
                } else {
                    updatedFiles[item.id] = { ...item.item, order: index };
                }
            });

            // Update database
            const userRef = doc(db, 'users', userData.uid);
            await updateDoc(userRef, {
                folders: updatedFolders,
                files: updatedFiles
            });

            // Update local state
            setFolders(updatedFolders);
            setFiles(updatedFiles);
            setUser({ ...userData, folders: updatedFolders, files: updatedFiles });

        } catch (error) {
            console.error('Error updating order:', error);
            Alert.alert('Error', 'Failed to update item order');
        }
    };

    return (
        <View style={
            { 
                borderRadius: 12,
                overflow: 'hidden',
                borderWidth: 3, 
                flex: 1,
                width: '100%', 
                height: '100%', 
                borderStyle: 'dashed',
                borderColor: isDragOver ? '#68AD10' : 'transparent' 
        }}>
            <LinearGradient 
                ref={mediaManagerRef}
                colors={['#3D7FE6', '#0a46a6']} 
                style={[
                    mediaManagerStyles.container,
                ]}
                className="media-manager-container"
                data-media-manager="true"
            >
                {/* Media Viewer */}
                <MediaViewer
                    visible={isMediaViewerVisible}
                    onClose={handleMediaViewerClose}
                    media={getCurrentFolderMedia()}
                    currentIndex={selectedMediaIndex}
                    onNavigate={setSelectedMediaIndex}
                />

                {/* Drag Over Indicator */}
                {isDragOver && (
                    <View style={mediaManagerStyles.dragOverIndicator}>
                        <View style={mediaManagerStyles.dragOverContent}>
                            <Ionicons name="cloud-upload" size={24} color="#ffffff" />
                            <Text style={mediaManagerStyles.dragOverText}>
                                Drop here to add to Media Manager
                            </Text>
                        </View>
                    </View>
                )}

                {/* Loading Overlay */}
                {loading && (
                    <View style={mediaManagerStyles.loadingOverlay}>
                        <View style={mediaManagerStyles.loadingContent}>
                            <Ionicons name="hourglass" size={22} color="#ffffff" />
                            <Text style={mediaManagerStyles.loadingText}>
                                Adding to Media Manager...
                            </Text>
                        </View>
                    </View>
                )}

                {/* Header - Two Rows */}
                {renderBreadcrumbRow()}
                {renderActionButtonsRow()}

                {/* Current Directory Contents */}
                <View style={[mediaManagerStyles.contentArea, (Platform.OS !== 'web' || (typeof window !== 'undefined' && window.innerWidth <= 820)) && mediaManagerStyles.contentAreaSingleColumn]}>
                    {/* Render filtered items sorted by order */}
                    {filteredItems.map(({ id, item, type }, index) =>
                        type === 'folder'
                            ? renderFolder(id, item, index)
                            : renderItem(id, item, index)
                    )}
                    {/* Empty state */}
                    {filteredItems.length === 0 && (
                        <View style={mediaManagerStyles.emptyState}>
                            <Ionicons name={currentFolderId ? "images-outline" : "folder-outline"} size={48} color="#ffffff" />
                            <Text style={mediaManagerStyles.emptyStateText}>
                                {searchQuery.trim() 
                                    ? `No files or folders found matching "${searchQuery}"`
                                    : currentFolderId
                                    ? "This folder is empty. Add some files or create subfolders!"
                                    : "No folders yet. Create your first folder to organize your media!"
                                }
                            </Text>
                        </View>
                    )}
                </View>


                {/* Create Folder Modal */}
                <Modal
                    visible={showCreateFolder}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => setShowCreateFolder(false)}
                >
                    <View style={mediaManagerStyles.modalOverlay}>
                        <View style={mediaManagerStyles.modalContent}>
                            <Text style={mediaManagerStyles.modalTitle}>Create New Folder</Text>

                            <TextInput
                                placeholder="Folder name"
                                value={newFolder.name}
                                onChangeText={(text) => setNewFolder({ ...newFolder, name: text })}
                                style={mediaManagerStyles.modalInput}
                            />

                            <Text style={mediaManagerStyles.modalLabel}>Choose folder color:</Text>

                            <View style={mediaManagerStyles.colorPickerContainer}>
                                {folderColors.map((color) => (
                                    <TouchableOpacity
                                        key={color}
                                        onPress={() => setNewFolder({ ...newFolder, color })}
                                        style={[
                                            mediaManagerStyles.colorPickerItem,
                                            newFolder.color === color && mediaManagerStyles.colorPickerItemSelected,
                                            { backgroundColor: color },
                                        ]}
                                    />
                                ))}
                            </View>

                            <View style={mediaManagerStyles.modalButtonRow}>
                                <TouchableOpacity
                                    onPress={() => setShowCreateFolder(false)}
                                    style={[mediaManagerStyles.modalButton, mediaManagerStyles.modalButtonCancel]}
                                >
                                    <Text style={mediaManagerStyles.modalButtonText}>Cancel</Text>
                                </TouchableOpacity>

                                <TouchableOpacity
                                    onPress={createFolder}
                                    style={[mediaManagerStyles.modalButton, mediaManagerStyles.modalButtonPrimary]}
                                >
                                    <Text style={[mediaManagerStyles.modalButtonText, mediaManagerStyles.modalButtonTextPrimary]}>Create</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </View>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal
                    visible={showDeleteConfirm !== null}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => {
                        setShowDeleteConfirm(null);
                        setDeleteConfirmText('');
                    }}
                >
                    <View style={mediaManagerStyles.modalOverlay}>
                        <View style={mediaManagerStyles.modalContent}>
                            <Text style={[mediaManagerStyles.modalTitle, { color: '#e74c3c' }]}>Delete Folder</Text>

                            {showDeleteConfirm && (() => {
                                const { foldersToDelete, filesToDelete } = getAllNestedItems(showDeleteConfirm);
                                return (
                                    <View>
                                        <Text style={[mediaManagerStyles.modalLabel, { textAlign: 'center' }]}>
                                            This will permanently delete the folder "{folders[showDeleteConfirm]?.name}" and ALL its contents:
                                        </Text>
                                        
                                        <View style={mediaManagerStyles.deleteWarning}>
                                            <Text style={mediaManagerStyles.deleteWarningTitle}>⚠️ Items to be deleted:</Text>
                                            
                                            <Text style={mediaManagerStyles.deleteWarningText}>
                                                📁 {foldersToDelete.length} folder{foldersToDelete.length !== 1 ? 's' : ''} (including subfolders)
                                            </Text>
                                            
                                            <Text style={mediaManagerStyles.deleteWarningTextLast}>
                                                📄 {filesToDelete.length} file{filesToDelete.length !== 1 ? 's' : ''}
                                            </Text>
                                        </View>
                                        
                                        <Text style={mediaManagerStyles.deleteConfirmText}>
                                            This action cannot be undone!
                                        </Text>
                                    </View>
                                );
                            })()}

                            <Text style={mediaManagerStyles.modalLabel}>Type the folder name to confirm:</Text>

                            <TextInput
                                placeholder="Enter folder name"
                                placeholderTextColor="#DDDDDD"
                                value={deleteConfirmText}
                                onChangeText={setDeleteConfirmText}
                                style={mediaManagerStyles.modalInput}
                            />

                            <View style={mediaManagerStyles.modalButtonRow}>
                                <TouchableOpacity
                                    onPress={() => {
                                        setShowDeleteConfirm(null);
                                        setDeleteConfirmText('');
                                    }}
                                    style={[mediaManagerStyles.modalButton, mediaManagerStyles.modalButtonCancel]}
                                >
                                    <Text style={mediaManagerStyles.modalButtonText}>Cancel</Text>
                                </TouchableOpacity>

                                <TouchableOpacity
                                    onPress={() => deleteFolder(showDeleteConfirm)}
                                    style={[mediaManagerStyles.modalButton, { backgroundColor: '#e74c3c' }]}
                                >
                                    <Text style={[mediaManagerStyles.modalButtonText, { color: '#ffffff', fontFamily: 'Glacial Indifference' }]}>Delete</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </View>
                </Modal>

                {/* Rename Modal */}
                <Modal
                    visible={showRenameModal !== null}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => {
                        setShowRenameModal(null);
                        setRenameText('');
                    }}
                >
                    <View style={mediaManagerStyles.modalOverlay}>
                        <View style={mediaManagerStyles.modalContent}>
                            <Text style={mediaManagerStyles.modalTitle}>
                                Rename {showRenameModal?.type === 'folder' ? 'Folder' : 'File'}
                            </Text>

                            <TextInput
                                placeholder="Enter new name"
                                value={renameText}
                                onChangeText={setRenameText}
                                style={mediaManagerStyles.modalInput}
                                autoFocus={true}
                                selectTextOnFocus={true}
                            />

                            <View style={mediaManagerStyles.modalButtonRow}>
                                <TouchableOpacity
                                    onPress={() => {
                                        setShowRenameModal(null);
                                        setRenameText('');
                                    }}
                                    style={[mediaManagerStyles.modalButton, mediaManagerStyles.modalButtonCancel]}
                                >
                                    <Text style={mediaManagerStyles.modalButtonText}>Cancel</Text>
                                </TouchableOpacity>

                                <TouchableOpacity
                                    onPress={() => {
                                        if (showRenameModal) {
                                            renameItem(showRenameModal.id, showRenameModal.type, renameText);
                                        }
                                    }}
                                    style={[mediaManagerStyles.modalButton, mediaManagerStyles.modalButtonPrimary]}
                                >
                                    <Text style={[mediaManagerStyles.modalButtonText, mediaManagerStyles.modalButtonTextPrimary]}>Rename</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </View>
                </Modal>

                {/* Color Picker Modal */}
                <Modal
                    visible={showColorModal !== null}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => {
                        setShowColorModal(null);
                        setSelectedColor('#0bb5df');
                    }}
                >
                    <View style={mediaManagerStyles.modalOverlay}>
                        <View style={mediaManagerStyles.modalContent}>
                            <Text style={mediaManagerStyles.modalTitle}>
                                Change Folder Color
                            </Text>

                            <Text style={[mediaManagerStyles.modalLabel, { textAlign: 'center' }]}>
                                Choose a new color for "{showColorModal?.item?.name}"
                            </Text>

                            <View style={[mediaManagerStyles.colorPickerContainer, { justifyContent: 'center', marginBottom: 25 }]}>
                                {folderColors.map((color) => (
                                    <TouchableOpacity
                                        key={color}
                                        onPress={() => setSelectedColor(color)}
                                        style={[
                                            mediaManagerStyles.colorPickerItemSmall,
                                            selectedColor === color && mediaManagerStyles.colorPickerItemSelected
                                        ]}
                                    />
                                ))}
                            </View>

                            <View style={mediaManagerStyles.modalButtonRow}>
                                <TouchableOpacity
                                    onPress={() => {
                                        setShowColorModal(null);
                                        setSelectedColor('#0bb5df');
                                    }}
                                    style={[mediaManagerStyles.modalButton, mediaManagerStyles.modalButtonCancel]}
                                >
                                    <Text style={mediaManagerStyles.modalButtonText}>Cancel</Text>
                                </TouchableOpacity>

                                <TouchableOpacity
                                    onPress={() => {
                                        if (showColorModal) {
                                            changeFolderColor(showColorModal.id, selectedColor);
                                        }
                                    }}
                                    style={[mediaManagerStyles.modalButton, { backgroundColor: selectedColor }]}
                                >
                                    <Text style={{ 
                                        color: '#ffffff', 
                                        fontSize: 16, 
                                        fontFamily: 'Glacial Indifference',
                                        textShadowColor: 'rgba(0,0,0,0.3)',
                                        textShadowOffset: { width: 1, height: 1 },
                                        textShadowRadius: 2
                                    }}>Change Color</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </View>
                </Modal>

                {/* Delete File Modal */}
                <Modal
                    visible={showDeleteFileModal !== null}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => setShowDeleteFileModal(null)}
                >
                    <View style={mediaManagerStyles.modalOverlay}>
                        <View style={[mediaManagerStyles.modalContent, { alignItems: 'center' }]}>
                            <View style={mediaManagerStyles.deleteFileIcon}>
                                <Ionicons name="trash" size={48} color="#e74c3c" />
                                <Text style={mediaManagerStyles.deleteFileTitle}>Delete File</Text>
                            </View>

                            <Text style={mediaManagerStyles.deleteFileDescription}>
                                Are you sure you want to delete this file?
                            </Text>

                            <View style={mediaManagerStyles.deleteFileInfo}>
                                <Text style={mediaManagerStyles.deleteFileInfoTitle}>"{showDeleteFileModal?.item?.name}"</Text>
                                {showDeleteFileModal?.item?.migratedFrom && (
                                    <Text style={mediaManagerStyles.deleteFileInfoSubtext}>
                                        Source: {showDeleteFileModal.item.migratedFrom}
                                    </Text>
                                )}
                                {showDeleteFileModal?.item?.uploadSource && (
                                    <Text style={mediaManagerStyles.deleteFileInfoSubtext}>
                                        Uploaded from: {showDeleteFileModal.item.uploadSource}
                                    </Text>
                                )}
                            </View>

                            <Text style={mediaManagerStyles.deleteFileWarning}>
                                This action cannot be undone. The file will be removed from your folder system.
                            </Text>

                            <View style={mediaManagerStyles.modalButtonRow}>
                                <TouchableOpacity
                                    onPress={() => setShowDeleteFileModal(null)}
                                    style={[mediaManagerStyles.modalButton, mediaManagerStyles.modalButtonCancel]}
                                >
                                    <Text style={[mediaManagerStyles.modalButtonText, { fontFamily: 'Glacial Indifference', color: '#333' }]}>Cancel</Text>
                                </TouchableOpacity>

                                <TouchableOpacity
                                    onPress={() => {
                                        if (showDeleteFileModal) {
                                            deleteFile(showDeleteFileModal.id);
                                            setShowDeleteFileModal(null);
                                        }
                                    }}
                                    style={[mediaManagerStyles.modalButton, { backgroundColor: '#e74c3c' }]}
                                >
                                    <Text style={[mediaManagerStyles.modalButtonText, { color: '#ffffff', fontFamily: 'Glacial Indifference', fontWeight: 'bold' }]}>Delete</Text>
                                </TouchableOpacity>
                            </View>
                        </View>
                    </View>
                </Modal>

                {/* Success Modal */}
                <Modal
                    visible={showSuccessModal !== null}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => setShowSuccessModal(null)}
                >
                    <View style={mediaManagerStyles.modalOverlay}>
                        <View style={[mediaManagerStyles.modalContent, mediaManagerStyles.successModal]}>
                            <Ionicons name="checkmark-circle" size={48} color="#2ecc71" />
                            <Text style={mediaManagerStyles.successTitle}>Success!</Text>

                            <Text style={mediaManagerStyles.successMessage}>{showSuccessModal}</Text>

                            <TouchableOpacity
                                onPress={() => setShowSuccessModal(null)}
                                style={mediaManagerStyles.successButton}
                            >
                                <Text style={mediaManagerStyles.successButtonText}>OK</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </Modal>

                {/* Error Modal */}
                <Modal
                    visible={showErrorModal !== null}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => setShowErrorModal(null)}
                >
                    <View style={mediaManagerStyles.modalOverlay}>
                        <View style={[mediaManagerStyles.modalContent, mediaManagerStyles.errorModal]}>
                            <Ionicons name="alert-circle" size={48} color="#e74c3c" />
                            <Text style={mediaManagerStyles.errorTitle}>Error</Text>

                            <Text style={mediaManagerStyles.errorMessage}>{showErrorModal}</Text>

                            <TouchableOpacity
                                onPress={() => setShowErrorModal(null)}
                                style={mediaManagerStyles.errorButton}
                            >
                                <Text style={mediaManagerStyles.errorButtonText}>OK</Text>
                            </TouchableOpacity>
                        </View>
                    </View>
                </Modal>

                {/* Context Menu */}
                {renderContextMenu()}

            </LinearGradient>
        </View>

    );
};

export default MediaManager;
